import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AlertBanner from '../../../components/common/AlertBanner/AlertBanner';
import Button from '../../../components/common/Button/Button';
import ClearFiltersButton from '../../../components/common/ClearFiltersButton/ClearFiltersButton';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import FilterBar from '../../../components/common/FilterBar/FilterBar';
import FilterChipsBar from '../../../components/common/FilterChipsBar/FilterChipsBar';
import { buildOptionFilterChips } from '../../../components/common/FilterChipsBar/buildFilterChips';
import FilterGroup from '../../../components/common/FilterGroup/FilterGroup';
import ListingBottomRow from '../../../components/common/ListingBottomRow/ListingBottomRow';
import MultiSelectFilter from '../../../components/common/MultiSelectFilter/MultiSelectFilter';
import PageContainer, { PageBody } from '../../../components/common/PageContainer/PageContainer';
import RecordCount from '../../../components/common/RecordCount/RecordCount';
import SearchFilterBar from '../../../components/common/SearchFilterBar/SearchFilterBar';
import SectionTitle from '../../../components/common/SectionTitle/SectionTitle';
import { type BreadcrumbItem, useBreadcrumbs } from '../../../contexts/BreadcrumbContext';
import { usePageSize } from '../../../hooks/usePageSize';
import { queryFilter, useQueryFilters } from '../../../hooks/useQueryFilters';
import { useStudentAssignedTrainings } from '../../../hooks/useStudentAssignedTrainings';
import useTrainings from '../../../hooks/useTrainings';
import { StudentTrainingService } from '../../../services';
import type { TrainingItem } from '../../../types/training';
import { mapStudentTrainingListItemToTrainingItem } from '../../../utils/studentTrainingList';
import TrainingList from '../../Training/components/TrainingList';
import TrainingRegisterModal from '../../Training/TrainingRegister/TrainingRegisterModal';
import styles from './StudentTraining.module.css';

const CATALOG_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

const STUDENT_TRAINING_QUERY_SCHEMA = {
  page: queryFilter.integer(1, { min: 1 }),
  search: queryFilter.string(),
  groups: queryFilter.array(),
};

const StudentTraining: React.FC = () => {
  const { id: studentId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [assignError, setAssignError] = useState('');
  const [editingTrainingId, setEditingTrainingId] = useState<string | null>(null);

  const { filters, setFilter, resetFilters } = useQueryFilters(
    STUDENT_TRAINING_QUERY_SCHEMA,
    { pageKey: 'page' },
  );
  const {
    page,
    pageSize,
    pageSizeOptions,
    handlePageChange,
    handlePageSizeChange,
  } = usePageSize('student_training_catalog_page_size', 20, CATALOG_PAGE_SIZE_OPTIONS);
  const { search: searchTerm, groups: selectedGroups } = filters;

  const {
    assignedTrainings,
    studentName,
    loading: assignedLoading,
    error: assignedError,
    refreshAssignedTrainings,
  } = useStudentAssignedTrainings(studentId);

  const {
    trainings,
    pagination,
    loading: catalogLoading,
    error: catalogError,
    totalTrainings,
    groupOptions,
  } = useTrainings(page, pageSize, { searchTerm, groups: selectedGroups });

  const hasActiveFilters = searchTerm.trim().length > 0 || selectedGroups.length > 0;
  const studentEditPath = `/alunas/${studentId}/editar`;

  useEffect(() => {
    const crumbs: BreadcrumbItem[] = [
      { label: 'Alunas', path: '/alunas' },
      { label: studentName || 'Aluna', path: studentEditPath },
      { label: 'Atribuir treino' },
    ];

    setBreadcrumbs(crumbs);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs, studentEditPath, studentName]);

  const handleClearFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const handleAssign = useCallback(async (training: TrainingItem) => {
    if (!studentId) return;

    setAssigningId(training.id);
    setAssignError('');
    try {
      const assigned = await StudentTrainingService.assign(studentId, training.id);
      refreshAssignedTrainings();
      setEditingTrainingId(assigned.id);
    } catch (error) {
      setAssignError(error instanceof Error ? error.message : 'Não foi possível atribuir o treino.');
    } finally {
      setAssigningId(null);
    }
  }, [refreshAssignedTrainings, studentId]);

  const filterChips = useMemo(() => buildOptionFilterChips({
    filterKey: 'groups',
    values: selectedGroups,
    options: groupOptions,
    onRemove: (groupValue) => {
      setFilter('groups', (currentValues) => currentValues.filter((value) => value !== groupValue));
    },
  }), [groupOptions, selectedGroups, setFilter]);

  const assignedItems = useMemo(
    () => assignedTrainings.map(mapStudentTrainingListItemToTrainingItem),
    [assignedTrainings],
  );

  if (!studentId) {
    return (
      <PageContainer>
        <EmptyState message="Aluna inválida para atribuir treino." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className={styles['header-row']}>
        <Button variant="ghost" size="small" icon="arrow_back" onClick={() => navigate(studentEditPath)}>
          Voltar para aluna
        </Button>
      </div>

      {assignError && <AlertBanner message={assignError} variant="error" />}

      <PageBody>
        <section className={styles.section}>
          <div className={styles['section-header']}>
            <SectionTitle as="h2" text="Treinos da aluna" />
            <RecordCount
              label="Atribuídos:"
              count={assignedTrainings.length}
              className={styles['record-count']}
            />
          </div>

          <TrainingList
            trainings={assignedItems}
            loading={assignedLoading}
            error={assignedError}
            emptyMessage="Nenhum treino atribuído a esta aluna."
            variant="student"
            onOpen={(training) => setEditingTrainingId(training.id)}
            showApplyAction={false}
            buildMenuItems={(training) => [{
              label: 'Editar',
              onClick: () => setEditingTrainingId(training.id),
            }]}
          />
        </section>

        <section className={[styles.section, styles['catalog-section']].join(' ')}>
          <SectionTitle as="h2" text="Catálogo de treinos" />

          <FilterBar gap="sm" pushFirstChild className={styles['filter-bar']}>
            <RecordCount
              label="Treinos cadastrados:"
              count={totalTrainings}
              className={styles['record-count']}
            />
            <FilterGroup>
              <SearchFilterBar
                searchValue={searchTerm}
                onSearchChange={(value) => setFilter('search', value)}
                searchPlaceholder="Pesquisar"
                loading={catalogLoading}
              />
            </FilterGroup>
            <FilterGroup>
              <MultiSelectFilter
                values={selectedGroups}
                onChange={(values) => setFilter('groups', values)}
                options={groupOptions}
                placeholder="Grupo"
                clearButtonLabel="Limpar filtro"
                hideChips
                showSelectionCount={false}
                className={styles['group-filter']}
              />
            </FilterGroup>
            {hasActiveFilters && (
              <ClearFiltersButton onClick={handleClearFilters} />
            )}
          </FilterBar>

          <div className={styles['chips-row']}>
            <FilterChipsBar chips={filterChips} />
          </div>

          <div className={styles['catalog-list']}>
            <TrainingList
              trainings={trainings}
              loading={catalogLoading}
              error={catalogError}
              emptyMessage={hasActiveFilters
                ? 'Nenhum treino encontrado com os filtros selecionados.'
                : 'Nenhum treino disponível no catálogo.'}
              highlightTerm={searchTerm}
              onApply={handleAssign}
              onOpen={(training) => navigate(`/treinos/${training.id}/editar`)}
              showApplyAction
              applyAlwaysVisible
              applyDisabled={Boolean(assigningId)}
              applyLabel={assigningId ? 'Atribuindo...' : 'Atribuir'}
            />
          </div>

          {trainings.length > 0 && (
            <div className={styles['catalog-bottom']}>
              <ListingBottomRow
                pageSize={pageSize}
                pageSizeOptions={pageSizeOptions}
                onPageSizeChange={handlePageSizeChange}
                pagination={pagination}
                loading={catalogLoading}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </section>
      </PageBody>
      <TrainingRegisterModal
        open={Boolean(editingTrainingId)}
        studentId={studentId}
        studentTrainingId={editingTrainingId ?? ''}
        onClose={() => {
          setEditingTrainingId(null);
          refreshAssignedTrainings();
        }}
      />
    </PageContainer>
  );
};

export default StudentTraining;
